export class WheelBrake extends Phaser.GameObjects.Container {
  private brake: Phaser.GameObjects.Image;
  private brakeCover: Phaser.GameObjects.Image;
  private brakePad: Phaser.GameObjects.Sprite;

  private brakeActivateSfx: Phaser.Sound.BaseSound;
  private brakeAssemblySfx: Phaser.Sound.BaseSound;
  private brakeDisassemblySfx: Phaser.Sound.BaseSound;

  private brakeShakeTween?: Phaser.Tweens.Tween;
  private padShakeTween?: Phaser.Tweens.Tween;
  private isShaking = false;

  private sparkEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;

  // Animation system
  private isAnimating = false;
  private animationTweens: Phaser.Tweens.Tween[] = [];

  constructor(scene: Phaser.Scene) {
    super(scene);

    // prepare the sounds
    this.brakeActivateSfx = scene.sound.add("sfx_brakeActivate");
    this.brakeAssemblySfx = scene.sound.add("sfx_brakeAssemble");
    this.brakeDisassemblySfx = scene.sound.add("sfx_brakeDisassemble");

    this.brake = new Phaser.GameObjects.Image(
      scene,
      0,
      0,
      "atlas_brake",
      "brake.png",
    );
    this.brake.setOrigin(0.5);
    this.add(this.brake);

    this.brakePad = new Phaser.GameObjects.Sprite(
      scene,
      0,
      0,
      "atlas_brake",
      "brake_pad.png",
    );
    this.brakePad.setOrigin(0.5);
    this.add(this.brakePad);

    this.createSparkSystem();

    this.brakeCover = new Phaser.GameObjects.Image(
      scene,
      0,
      0,
      "atlas_brake",
      "brake_cover.png",
    );
    this.brakeCover.setOrigin(0.5);
    this.add(this.brakeCover);
  }

  public Resize(width: number, height: number): void {
    this.setSize(width, height);
    this.brake.setDisplaySize(width, height);
    this.brakeCover.setDisplaySize(width, height);
    this.brakePad.setDisplaySize(width, height);
  }

  public updateHeat(usagePercentage: number): void {
    if (usagePercentage < 50) {
      // Below 50%, keep original color
      this.brakePad.clearTint();
    } else {
      const heatIntensity = (usagePercentage - 50) / 50; // 0 to 1

      // Calculate RGB values for orange-red hot effect
      const red = 255;
      const green = Math.floor(165 * (1 - heatIntensity * 0.7)); // Start orange (165), fade to deep red
      const blue = Math.floor(50 * (1 - heatIntensity)); // Minimal blue, fades to 0

      const color = (red << 16) | (green << 8) | blue;
      this.brakePad.setTint(color);
    }
  }

  /**
   * Start braking effects (shaking and sparks)
   */
  public startBrakingEffects(): void {
    // Start shaking animation
    if (!this.isShaking) {
      this.isShaking = true;

      this.brakeActivateSfx.play({ rate: Phaser.Math.FloatBetween(1.2, 1.3) });

      // Shake brake and brake cover together
      this.brakeShakeTween = this.scene.tweens.add({
        targets: [this.brake, this.brakeCover],
        x: { from: 0, to: "+=2" },
        y: { from: 0, to: "+=1" },
        duration: 50,
        ease: "Power2",
        yoyo: true,
        repeat: -1,
      });

      // Shake brake pad separately with different pattern
      this.padShakeTween = this.scene.tweens.add({
        targets: this.brakePad,
        x: { from: 0, to: "+=1.5" },
        y: { from: 0, to: "+=2.5" },
        duration: 75,
        ease: "Power1",
        yoyo: true,
        repeat: -1,
      });
    }

    if (!this.sparkEmitter) return;

    if (!this.sparkEmitter.emitting) {
      this.sparkEmitter.start();
    }
  }

  /**
   * Stop all braking effects (shaking and sparks)
   */
  public stopBrakingEffects(): void {
    // Stop shaking
    if (this.isShaking) {
      this.isShaking = false;

      // Stop and clean up tweens
      if (this.brakeShakeTween) {
        this.brakeShakeTween.stop();
        this.brakeShakeTween = undefined;
      }
      if (this.padShakeTween) {
        this.padShakeTween.stop();
        this.padShakeTween = undefined;
      }

      // Reset positions
      this.brake.setPosition(0, 0);
      this.brakeCover.setPosition(0, 0);
      this.brakePad.setPosition(0, 0);
    }

    if (this.sparkEmitter) {
      this.sparkEmitter.stop();
    }
  }

  /**
   * Create spark particle system
   */
  private createSparkSystem(): void {
    // Create spark texture based on brake pad size
    if (!this.scene.textures.exists("spark")) {
      const sparkSize = Math.max(
        2,
        Math.floor(this.brakePad.displayWidth * 0.05),
      );
      const sparkRadius = Math.floor(sparkSize / 2);

      const graphics = this.scene.add.graphics();
      graphics.fillStyle(0xffffff);
      graphics.fillCircle(sparkRadius, sparkRadius, sparkRadius);
      graphics.generateTexture("spark", sparkSize, sparkSize);
      graphics.destroy();
    }

    this.sparkEmitter = this.scene.add.particles(0, 0, "spark", {
      speed: { min: 300, max: 600 },
      scale: { start: 2.0, end: 0.2 },
      x: { min: -this.brakePad.displayWidth / 2, max: 0 },
      y: {
        min: -this.brakePad.displayHeight / 2,
        max: this.brakePad.displayHeight,
      },
      lifespan: 400,
      alpha: { start: 1, end: 0 },
      tint: [0xffffff, 0xffaa00, 0xff6600],
      gravityY: 200,
      emitting: false,
      frequency: 30,
      quantity: 5,
      blendMode: "ADD",
      angle: { min: 140, max: 315 },
    });

    this.add(this.sparkEmitter);
  }

  /**
   * Animate the brake assembling itself onto the wheel
   */
  public playAssemblyAnimation(onComplete?: () => void): void {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Store the current scales to restore them after animation
    const targetScaleX = this.brake.scaleX;
    const targetScaleY = this.brake.scaleY;

    const assemblyRadius = this.brake.displayWidth / 2;
    const angles = [225, 210, 60];
    const parts = [this.brake, this.brakePad, this.brakeCover];

    parts.forEach((part, index) => {
      const angle = Phaser.Math.DegToRad(angles[index]);
      const startX = Math.cos(angle) * assemblyRadius;
      const startY = Math.sin(angle) * assemblyRadius;

      part.setPosition(startX, startY);
      part.setAlpha(0);
      part.setScale(0.2);
      part.setRotation(Phaser.Math.FloatBetween(-1, 1));
    });

    this.brakeAssemblySfx.play({ rate: Phaser.Math.FloatBetween(0.95, 1) });

    // Animate each part flying in and assembling
    parts.forEach((part, index) => {
      const delay = index * 50; // Stagger the animations

      const tween = this.scene.tweens.add({
        targets: part,
        x: 0,
        y: 0,
        alpha: 1,
        scaleX: targetScaleX,
        scaleY: targetScaleY,
        rotation: 0,
        duration: 200,
        delay: delay,
        ease: "Back.easeOut",
        onComplete: () => {
          // When the last part finishes, complete the animation
          if (index === parts.length - 1) {
            this.isAnimating = false;
            if (onComplete) onComplete();
          }
        },
      });

      this.animationTweens.push(tween);
    });
  }

  public playDisassemblyAnimation(onComplete?: () => void): void {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Stop any brake effects first
    this.stopBrakingEffects();

    const parts = [this.brake, this.brakePad, this.brakeCover];
    let completedParts = 0;

    this.brakeDisassemblySfx.play({ rate: Phaser.Math.FloatBetween(0.95, 1) });

    parts.forEach((part, index) => {
      const horizontalDrift = Phaser.Math.FloatBetween(
        -this.brake.displayWidth / 2,
        this.brake.displayWidth / 2,
      );
      const fallDistance =
        this.brake.displayHeight * 2 +
        Phaser.Math.FloatBetween(0, this.brake.displayHeight / 2); // Fall down with some variation

      const tumbleRotation = Phaser.Math.FloatBetween(
        -Math.PI / 2,
        Math.PI / 2,
      );

      const tween = this.scene.tweens.add({
        targets: part,
        x: horizontalDrift,
        y: fallDistance,
        alpha: 0,
        scaleX: 0.8, // Don't shrink too much - parts should look like they're falling away
        scaleY: 0.8,
        rotation: part.rotation + tumbleRotation,
        duration: 500 + index * 50, // Stagger the fall timing
        delay: index * 100, // Parts fall one after another
        ease: "Cubic.easeIn", // Accelerating fall due to gravity
        onComplete: () => {
          completedParts++;
          if (completedParts === parts.length) {
            this.isAnimating = false;
            if (onComplete) onComplete();
          }
        },
      });

      this.animationTweens.push(tween);
    });
  }

  /**
   * Check if brake is currently animating
   */
  public getIsAnimating(): boolean {
    return this.isAnimating;
  }

  /**
   * Stop all animations and clean up tweens
   */
  private stopAllAnimations(): void {
    this.animationTweens.forEach((tween) => {
      if (tween && !tween.isDestroyed()) {
        tween.destroy();
      }
    });
    this.animationTweens.length = 0;
    this.isAnimating = false;
  }

  /**
   * Destructor - clean up all tweens and particles
   */
  public override destroy(): void {
    this.stopBrakingEffects();
    this.stopAllAnimations();
    if (this.sparkEmitter) {
      this.sparkEmitter.destroy();
    }

    // Clean up spark texture
    if (this.scene.textures.exists("spark")) {
      this.scene.textures.remove("spark");
    }

    super.destroy();
  }
}
